import React from "react";
import { StyleSheet, View } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { FilterChip } from "./FilterChip";
import { useThemeColor } from "@/hooks/useThemeColor";
import { IActivityFilterCriteria } from "common247";

export interface IActivityFilterComponentProps {
  criteria: IActivityFilterCriteria;
  onChange: (updated: IActivityFilterCriteria) => void;
}

const ACTIONS: ("create" | "update" | "delete")[] = ["create", "update", "delete"];

const ENTITY_TYPES = [
  "memory",
  "media",
  "contact",
  "group",
  "share",
  "message",
];

/**
 * Filter controls specific to the Activity Log stream.
 */
export function ActivityFilter({
  criteria,
  onChange,
}: IActivityFilterComponentProps): React.JSX.Element {
  const textMuted = useThemeColor({}, "textMuted");

  const currentActions = criteria.actions || [];
  const currentEntityTypes = criteria.entityTypes || [];

  const handleActionToggle = (action: "create" | "update" | "delete"): void => {
    const updated = currentActions.includes(action)
      ? currentActions.filter((a) => a !== action)
      : [...currentActions, action];
    onChange({ ...criteria, actions: updated.length > 0 ? updated : undefined });
  };

  const handleEntityTypeToggle = (entityType: string): void => {
    const updated = currentEntityTypes.includes(entityType)
      ? currentEntityTypes.filter((t) => t !== entityType)
      : [...currentEntityTypes, entityType];
    onChange({ ...criteria, entityTypes: updated.length > 0 ? updated : undefined });
  };

  return (
    <View style={styles.container}>
      {/* Date Range */}
      <View style={styles.row}>
        <View style={styles.col}>
          <ThemedText style={[styles.label, { color: textMuted }]}>From Date</ThemedText>
          <ThemedInput
            value={criteria.dateFrom ? String(criteria.dateFrom) : ""}
            onChangeText={(txt) => onChange({ ...criteria, dateFrom: txt.trim() || undefined })}
            placeholder="e.g. 2026, 01-Jan-2026"
            style={styles.input}
          />
        </View>
        <View style={styles.col}>
          <ThemedText style={[styles.label, { color: textMuted }]}>To Date</ThemedText>
          <ThemedInput
            value={criteria.dateTo ? String(criteria.dateTo) : ""}
            onChangeText={(txt) => onChange({ ...criteria, dateTo: txt.trim() || undefined })}
            placeholder="e.g. 2026, 31-Dec-2026"
            style={styles.input}
          />
        </View>
      </View>

      {/* Action types */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Action Type</ThemedText>
        <View style={styles.chipsWrap}>
          {ACTIONS.map((action) => (
            <FilterChip
              key={action}
              label={action.toUpperCase()}
              selected={currentActions.includes(action)}
              onPress={() => handleActionToggle(action)}
              compact
            />
          ))}
        </View>
      </View>

      {/* Target entity categories */}
      <View style={styles.section}>
        <ThemedText style={[styles.label, { color: textMuted }]}>Target Entity</ThemedText>
        <View style={styles.chipsWrap}>
          {ENTITY_TYPES.map((type) => (
            <FilterChip
              key={type}
              label={type.toUpperCase()}
              selected={currentEntityTypes.includes(type)}
              onPress={() => handleEntityTypeToggle(type)}
              compact
            />
          ))}
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 4,
  },
  row: {
    flexDirection: "row",
    gap: 10,
    marginBottom: 8,
  },
  col: {
    flex: 1,
  },
  section: {
    marginBottom: 8,
  },
  label: {
    fontSize: 12,
    fontWeight: "600",
    marginBottom: 4,
  },
  input: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    fontSize: 13,
  },
  chipsWrap: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
});
